import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ChevronDown, ChevronUp, Mail, Phone, Calendar, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

const SERVICE_LABELS = {
  self_drive: "Self-drive",
  chauffeur: "Chauffeur",
  airport: "Airport pickup",
  long_term: "Long-term",
  corporate: "Corporate",
  event: "Event / Wedding",
};

const statusClass = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  quoted: "bg-purple-100 text-purple-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-gray-100 text-gray-600",
};

export default function AdminQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/quotes").then((r) => setQuotes(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8" data-testid="admin-quotes-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="eyebrow">Custom Quotes</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Quote requests</h1>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>
      ) : quotes.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">No quote requests yet.</div>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => {
            const isOpen = expanded === q.id;
            return (
              <div key={q.id} className="bg-white border border-gray-200" data-testid={`quote-row-${q.id}`}>
                <div className="p-4 grid grid-cols-12 gap-4 items-center cursor-pointer hover:bg-[#f7f8fa]" onClick={() => setExpanded(isOpen ? null : q.id)}>
                  <div className="col-span-3">
                    <div className="font-display font-semibold text-[#0d2040]">{q.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail size={11} /> {q.email}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} /> {q.phone}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-widest text-gray-500">Service</div>
                    <div className="text-sm font-semibold text-[#0d2040]">{SERVICE_LABELS[q.service_type] || q.service_type}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-widest text-gray-500">Vehicle</div>
                    <div className="text-sm text-[#0d2040] capitalize">{(q.vehicle_category || "").replace("_", " ")}</div>
                  </div>
                  <div className="col-span-2 text-xs text-gray-600">
                    {q.start_date && <div className="flex items-center gap-1"><Calendar size={11} /> {q.start_date} → {q.end_date || "?"}</div>}
                    {q.passengers && <div className="flex items-center gap-1 mt-0.5"><Users size={11} /> {q.passengers} pax</div>}
                    {q.destination && <div className="flex items-center gap-1 mt-0.5"><MapPin size={11} /> {q.destination}</div>}
                  </div>
                  <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={q.status}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border-0 w-full ${statusClass[q.status] || "bg-gray-100"}`}
                      data-testid={`quote-status-select-${q.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="text-[0.65rem] text-gray-400 mt-1">{(q.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="col-span-1 text-right">
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-gray-100 p-5 bg-[#f7f8fa] grid md:grid-cols-2 gap-5 text-sm">
                    <div>
                      <Detail label="Pickup" value={q.pickup_location} />
                      <Detail label="Destination" value={q.destination} />
                      <Detail label="Start" value={q.start_date} />
                      <Detail label="End" value={q.end_date} />
                      <Detail label="Duration" value={q.duration} />
                      <Detail label="Passengers" value={q.passengers} />
                      <Detail label="Budget" value={q.budget} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-gray-500">Message</div>
                      <p className="text-[#0d2040] mt-1 whitespace-pre-wrap leading-relaxed">{q.message}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1 border-b border-gray-200 last:border-0">
      <span className="text-xs uppercase tracking-widest text-gray-500">{label}</span>
      <span className="text-[#0d2040] font-medium">{value}</span>
    </div>
  );
}
